// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, redirect, useBlocker, useNavigate } from "@tanstack/react-router";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useStoreDetailQuery } from "@/api/hooks/use-stores";
import { UnsavedChangesDialog } from "@/components/shared/unsaved-changes-dialog";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { CutOffTimesTab } from "@/features/cut-off-times/cut-off-times-tab";
import { StoreActivityTab } from "@/features/stores/store-activity-tab";
import { StoreDevicesTab } from "@/features/stores/store-devices-tab";
import { StoreHealthTab } from "@/features/stores/store-health-tab";
import { StoreImportsTab } from "@/features/stores/store-imports-tab";
import { StoreInfoTab } from "@/features/stores/store-info-tab";
import { StoreMapTab } from "@/features/stores/store-map-tab";
import { StoreScheduleTab, type StoreScheduleTabHandle } from "@/features/stores/store-schedule-tab";
import { StoreScorecardTab } from "@/features/stores/store-scorecard-tab";
import { StoreSettingsTab } from "@/features/stores/store-settings-tab";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";

// Tabs that used to live here and now live on `/stores/$storeName/edit`. Old links and bookmarks
// carrying `?t=edit|operations|hardware` are forwarded rather than silently dropped to Info.
const MOVED_TABS: Record<string, string | undefined> = {
  edit: undefined, // General is the edit page's default tab, so it needs no `t`
  operations: "operations",
  hardware: "hardware",
};

export const Route = createFileRoute("/_authenticated/stores/$storeName/")({
  beforeLoad: ({ params, search }) => {
    if (search.t && search.t in MOVED_TABS) {
      const movedTo = MOVED_TABS[search.t];
      throw redirect({
        to: "/stores/$storeName/edit",
        params,
        search: movedTo ? { t: movedTo } : {},
      });
    }
  },
  component: StoreDetailPage,
});

function StoreDetailPage() {
  const { storeName } = Route.useParams();
  const { t: tab, f } = Route.useSearch();
  const { hasPermission } = useAuth();
  const { debugMode } = useDebugMode();
  const navigate = useNavigate();
  const { t } = useTranslation("back-office");
  const { data: storeRaw } = useStoreDetailQuery(storeName);

  const canViewScorecard = hasPermission(PAGE_PERMISSIONS.storesScorecard);
  const canViewHealth = hasPermission(PAGE_PERMISSIONS.storesHealth);
  const canViewSchedule = hasPermission(PAGE_PERMISSIONS.storesSchedule);
  const canViewCutOffTimes = hasPermission(PAGE_PERMISSIONS.storesCutOffTimes);
  const canViewDevices = hasPermission(PAGE_PERMISSIONS.storesDevices);
  const canViewMap = hasPermission(PAGE_PERMISSIONS.storesMap);
  const canViewImports = hasPermission(PAGE_PERMISSIONS.ingressImports);
  const canViewSettings = hasPermission(PAGE_PERMISSIONS.storesSettings);
  // Activity log is work-in-progress — debug-mode only (in addition to the view permission).
  const canViewActivity = hasPermission(PAGE_PERMISSIONS.storesActivity) && debugMode;

  const tabAllowed: Record<string, boolean> = {
    info: true,
    scorecard: canViewScorecard,
    health: canViewHealth,
    schedule: canViewSchedule,
    "cut-off-times": canViewCutOffTimes,
    devices: canViewDevices,
    map: canViewMap,
    imports: canViewImports,
    settings: canViewSettings,
    activity: canViewActivity,
  };
  const requestedTab = tab ?? "info";
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";

  const store = storeRaw as Record<string, unknown> | undefined;
  const chainName = store?.chainName as string | undefined;

  // Schedule is the one tab left here that edits, so it keeps its own unsaved-changes guard.
  const [isScheduleDirty, setIsScheduleDirty] = useState(false);
  const scheduleTabRef = useRef<StoreScheduleTabHandle>(null);
  const blocker = useBlocker({
    shouldBlockFn: () => isScheduleDirty,
    enableBeforeUnload: () => isScheduleDirty,
    withResolver: true,
  });

  function handleTabChange(tab: string) {
    navigate({
      to: "/stores/$storeName",
      params: { storeName },
      search: tab === "info" ? {} : { t: tab },
      replace: true,
    });
  }

  return (
    <div>
      <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
        <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
          <TabsList>
            <TabsIndicator />
            <TabsTrigger value="info">{t("tabs.info")}</TabsTrigger>
            {canViewScorecard && <TabsTrigger value="scorecard">{t("tabs.scorecard")}</TabsTrigger>}
            {canViewHealth && <TabsTrigger value="health">{t("tabs.health")}</TabsTrigger>}
            {canViewSchedule && <TabsTrigger value="schedule">{t("tabs.schedule")}</TabsTrigger>}
            {canViewCutOffTimes && <TabsTrigger value="cut-off-times">{t("tabs.cutOffTimes")}</TabsTrigger>}
            {canViewDevices && <TabsTrigger value="devices">{t("tabs.devices")}</TabsTrigger>}
            {canViewMap && <TabsTrigger value="map">{t("tabs.map")}</TabsTrigger>}
            {canViewImports && <TabsTrigger value="imports">{t("tabs.imports")}</TabsTrigger>}
            {canViewSettings && <TabsTrigger value="settings">{t("tabs.settings")}</TabsTrigger>}
            {canViewActivity && <TabsTrigger value="activity">{t("tabs.activityLog")}</TabsTrigger>}
          </TabsList>
        </div>
        <TabsContent value="info" className="space-y-4">
          <StoreInfoTab storeName={storeName} />
        </TabsContent>
        {canViewScorecard && (
          <TabsContent value="scorecard" className="space-y-4">
            <StoreScorecardTab storeName={storeName} />
          </TabsContent>
        )}
        {canViewHealth && (
          <TabsContent value="health" className="space-y-4">
            <StoreHealthTab storeName={storeName} />
          </TabsContent>
        )}
        {canViewSchedule && (
          <TabsContent value="schedule" className="space-y-4">
            <StoreScheduleTab ref={scheduleTabRef} storeName={storeName} onDirtyChange={setIsScheduleDirty} />
          </TabsContent>
        )}
        {canViewCutOffTimes && (
          <TabsContent value="cut-off-times" className="space-y-4">
            <CutOffTimesTab
              target={{ kind: "store", storeName }}
              initialFilters={activeTab === "cut-off-times" ? f : undefined}
            />
          </TabsContent>
        )}
        {canViewDevices && (
          <TabsContent value="devices" className="space-y-4">
            <StoreDevicesTab storeName={storeName} />
          </TabsContent>
        )}
        {canViewSettings && (
          <TabsContent value="settings" className="space-y-4">
            <StoreSettingsTab storeName={storeName} initialFilters={activeTab === "settings" ? f : undefined} />
          </TabsContent>
        )}
        {canViewMap && (
          <TabsContent value="map" className="space-y-4">
            <StoreMapTab storeName={storeName} />
          </TabsContent>
        )}
        {canViewImports && (
          <TabsContent value="imports" className="space-y-4">
            <StoreImportsTab
              storeName={storeName}
              storeId={store?.storeId as number | undefined}
              chainName={chainName}
              initialFilters={activeTab === "imports" ? f : undefined}
            />
          </TabsContent>
        )}
        {canViewActivity && (
          <TabsContent value="activity" className="space-y-4">
            <StoreActivityTab storeName={storeName} initialFilters={activeTab === "activity" ? f : undefined} />
          </TabsContent>
        )}
      </Tabs>

      <UnsavedChangesDialog
        open={blocker.status === "blocked"}
        onStay={() => blocker.reset?.()}
        onDiscard={() => {
          scheduleTabRef.current?.reset();
          blocker.proceed?.();
        }}
      />
    </div>
  );
}
